import React, { Fragment, PropsWithChildren, useEffect, useImperativeHandle, useRef, useState } from 'react';
import DrawerHandler from './DrawerHandler';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { classNames, NVL } from '../utils/classNames';
import FormButton from '../button/FormButton';

export type DrawerProps = {
	onClose?: any,
	title: string
	size?: 'small' | 'normal' | 'large' | 'huge'
	actions?: any,
	isLoading?: boolean,
	noCloseOnOverlayClick?: boolean,
	// Called instead of closing when the user asks to close (the X, Close, the overlay or Escape), for example to ask
	// before unsaved changes are lost. The parent closes the drawer with the ref. The ref close() never calls it.
	onCloseRequest?: () => void,
	zIndexClass?: string,
	isOpened?: boolean,
	ref: any,

}

const Drawer: React.FunctionComponent<PropsWithChildren<DrawerProps>> = React.forwardRef(({
																																														children,
																																														title,
																																														size = 'normal',
																																														onClose,
																																														isLoading,
																																														actions,
																																														noCloseOnOverlayClick = false,
																																														onCloseRequest,
																																														zIndexClass = 'z-10',
																																														isOpened = false,
																																													}, ref: React.Ref<DrawerHandler>) => {

	const [isActive, setIsActive] = useState<boolean>(false);
	const [sizeClassNames, setSizeClassNames] = useState<string>('');
	const closeTimeoutRef = useRef<number | null>(null);

	useEffect(() => {
		if (isOpened) {
			open();
		}
	}, [isOpened]);

	useEffect(() => {
		switch (size) {
			case 'small': {
				setSizeClassNames('max-w-md');
				break;
			}
			case 'normal': {
				setSizeClassNames('max-w-lg');
				break;
			}
			case 'large': {
				setSizeClassNames('max-w-2xl');
				break;
			}
			case 'huge': {
				setSizeClassNames('max-w-4xl');
				break;
			}
		}
	}, []);

	const open = () => {
		setIsActive(true);
	};
	const close = () => {
		setIsActive(false);
		if (closeTimeoutRef.current != null) {
			window.clearTimeout(closeTimeoutRef.current);
		}

		if (onClose != null) {
			closeTimeoutRef.current = window.setTimeout(() => {
				onClose();
			}, 1000);
		}
	};

	// A close action of the user: the X, the Close button, the overlay and Escape.
	const requestClose = () => {
		if (onCloseRequest != null) {
			onCloseRequest();
		} else {
			close();
		}
	};

	useImperativeHandle(ref, () => {
		return {
			open: open,
			close: close,
			isOpen: isActive,
		};
	});

	return <Transition show={isActive} as={Fragment}>
		<Dialog as="div" className={`relative ${zIndexClass}`} onClose={!noCloseOnOverlayClick ? requestClose : () => undefined}>
			<TransitionChild
				as={Fragment}
				enter="ease-in-out duration-500"
				enterFrom="opacity-0"
				enterTo="opacity-100"
				leave="ease-in-out duration-500"
				leaveFrom="opacity-100"
				leaveTo="opacity-0"
			>
				<div className="fixed inset-0 bg-slate-500 bg-opacity-75 transition-opacity" />
			</TransitionChild>

			<div className="fixed inset-0 overflow-hidden">
				<div className="absolute inset-0 overflow-hidden">
					<div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
						<TransitionChild
							as={Fragment}
							enter="transform transition ease-in-out duration-500 sm:duration-700"
							enterFrom="translate-x-full"
							enterTo="translate-x-0"
							leave="transform transition ease-in-out duration-500 sm:duration-700"
							leaveFrom="translate-x-0"
							leaveTo="translate-x-full"
						>
							<DialogPanel
								className={classNames('pointer-events-auto w-screen', sizeClassNames)}>
								<div className="flex h-full flex-col  bg-white shadow-xl">
									{/* Header */}
									<div className="flex-shrink-0 bg-slate-100 px-4 py-6 sm:px-6">
										<div className="flex items-start justify-between">
											<h2 id="slide-over-heading"
												className="text-base font-semibold leading-6 text-slate-900">
												{title}
											</h2>
											<div className="ml-3 flex h-7 items-center">
												<button
													type="button"
													className="rounded-md text-slate-400 hover:text-slate-500 focus:ring-2 focus:ring-blue-500"
													onClick={requestClose}
												>
													<div className="sr-only">Close panel</div>
													<XMarkIcon className="h-6 w-6" aria-hidden="true" />
												</button>
											</div>
										</div>
									</div>

									<div className="flex-1 overflow-y-scroll">
										{/* Main Body */}
										{isActive && children}
									</div>
									{/* Action buttons */}
									<div className="flex-shrink-0 border-t border-slate-200 px-4 py-4 sm:px-6">
										<div className="flex justify-end space-x-3">
											<FormButton state="white" onClick={requestClose} isLoading={isLoading}>
												Close
											</FormButton>
											{actions}
										</div>
									</div>
								</div>
							</DialogPanel>
						</TransitionChild>
					</div>
				</div>
			</div>
		</Dialog>
	</Transition>;
});

export default Drawer;
